/* eslint-disable promise/catch-or-return */
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { COMPOSE_QUOTE, COMPOSE_REPLY, setComposeToStatus } from 'soapbox/actions/compose';
import { fetchDraftStatuses, saveDraftStatus } from 'soapbox/actions/draft-statuses';
import { getSettings } from 'soapbox/actions/settings';
import { fetchStatus } from 'soapbox/actions/statuses';
import { checkComposeContent } from 'soapbox/components/modal-root';
import { HStack, Stack, Text } from 'soapbox/components/ui';
import ComposeForm from 'soapbox/features/compose/components/compose-form';
import { useAppDispatch, useCompose } from 'soapbox/hooks';
import { makeGetStatus, selectOwnAccount } from 'soapbox/selectors';
import { getFeatures } from 'soapbox/utils/features';

import { buildStatus as buildDraftStatus } from '../draft-statuses/builder';

import type { Compose } from 'soapbox/reducers/compose';

const getStatus = makeGetStatus();

const ComposePage = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const [saved, setSaved] = useState(false);

  const compose = useCompose('compose-modal');

  const { id: statusId, privacy, group_id: groupId } = compose!;

  const inReplyTo = params.get('in_reply_to');
  const quote = params.get('quote');
  const draftId = params.get('draft_id');

  const heading = useMemo(() => {
    if (statusId) {
      return <FormattedMessage id='navigation_bar.compose_edit' defaultMessage='Edit post' />;
    } else if (privacy === 'direct') {
      return <FormattedMessage id='navigation_bar.compose_direct' defaultMessage='Direct message' />;
    } else if (inReplyTo && groupId) {
      return <FormattedMessage id='navigation_bar.compose_group_reply' defaultMessage='Reply to group post' />;
    } else if (groupId) {
      return <FormattedMessage id='navigation_bar.compose_group' defaultMessage='Compose to group' />;
    } else if (inReplyTo) {
      return <FormattedMessage id='navigation_bar.compose_reply' defaultMessage='Reply to post' />;
    } else if (quote) {
      return <FormattedMessage id='navigation_bar.compose_quote' defaultMessage='Quote post' />;
    } else {
      return <FormattedMessage id='navigation_bar.compose' defaultMessage='Compose new post' />;
    }
  }, []);

  useEffect(() => {
    dispatch(fetchDraftStatuses());
    if (inReplyTo) dispatch(fetchStatus(inReplyTo, intl))
      .then((_) => dispatch((dispatch, getState) => {
        const state  = getState();
        const instance = state.instance;
        const { explicitAddressing } = getFeatures(instance);

        const status = getStatus(state, { id: inReplyTo });

        dispatch({
          type: COMPOSE_REPLY,
          id: 'compose-modal',
          status: status,
          account: selectOwnAccount(state),
          explicitAddressing,
          preserveSpoilers: !!getSettings(state).get('preserveSpoilers'),
        });
      }));
    else if (quote) dispatch(fetchStatus(quote, intl))
      .then((_) => dispatch((dispatch, getState) => {
        const state  = getState();
        const instance = state.instance;
        const { explicitAddressing } = getFeatures(instance);

        const status = getStatus(state, { id: quote });

        dispatch({
          type: COMPOSE_QUOTE,
          id: 'compose-modal',
          status: status,
          account: selectOwnAccount(state),
          explicitAddressing,
        });
      }));
    else if (draftId) dispatch(fetchDraftStatuses())
      .then(() => dispatch((dispatch, getState) => {
        const state = getState();
        const draftStatus = state.draft_statuses.get(draftId);

        if (draftStatus) {
          const status = buildDraftStatus(state, draftStatus);

          dispatch(setComposeToStatus(status as any, draftStatus.text, draftStatus.spoiler_text, draftStatus.content_type, false, draftStatus.draft_id, draftStatus.editorState));
        }
      }));
  }, []);

  useEffect(() => {
    let previous: Compose | undefined;
    const interval = setInterval(() => {
      dispatch((dispatch, getState) => {
        const compose = getState().compose.get('compose-modal');
        if (previous !== compose && checkComposeContent(compose)) {
          dispatch(saveDraftStatus('compose-modal'));

          previous = compose;

          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        }
      });
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Stack className='h-full p-4' space={2}>
      <HStack space={2} alignItems='center' justifyContent='between'>
        <h3 className='grow-0 truncate text-lg font-bold leading-6 text-gray-900 dark:text-white'>
          {heading}
        </h3>
        {saved && (
          <Text theme='success' tag='span' className='leading-none'>
            <FormattedMessage id='generic.saved' defaultMessage='Saved' />
          </Text>
        )}
      </HStack>
      {(draftId ? !!compose.draft_id : true) && <ComposeForm id='compose-modal' onSubmit={() => window.close()} fullScreen />}
    </Stack>
  );
};

export default ComposePage;
